import React from "react";
import { useNotificationContext } from "../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { Check, Trash2, CheckCircle, Bell } from "lucide-react";

const NotificationsPage: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead, removeNotification } = useNotificationContext();
  const navigate = useNavigate();

  const handleNotificationClick = (id: string, link?: string | null) => {
    markAsRead(id);
    if (link) {
      navigate(link);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
          <Bell className="text-primary-600" size={24} />
          Notifications
        </h2>
        
        {notifications.length > 0 && (
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-2 h-10 bg-white border border-gray-200 text-neutral-700 px-4 rounded-lg text-sm font-medium shadow-sm hover:bg-gray-50 transition-colors shrink-0"
          >
            <CheckCircle size={18} className="text-gray-500" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-xs border border-gray-200 overflow-hidden">
        {notifications.length === 0 ? (
          <div className="p-8 text-center flex flex-col items-center justify-center text-gray-500">
            <Bell size={48} className="text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-1">No notifications</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif.id, notif.link)}
                className={`p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-colors ${
                  notif.link ? "cursor-pointer hover:bg-gray-50" : ""
                } ${notif.is_read ? "bg-white" : "bg-primary-50/30"}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`text-sm font-semibold truncate ${notif.is_read ? "text-gray-900" : "text-primary-700"}`}>
                      {notif.title}
                    </h4>
                    {!notif.is_read && (
                      <span className="bg-primary-100 text-primary-700 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                        New
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 break-words line-clamp-2">
                    {notif.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(notif.createdAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  {!notif.is_read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notif.id);
                      }}
                      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-primary-600 transition-colors"
                      title="Mark as read"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(notif.id);
                    }}
                    className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Delete notification"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
